import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Box, BoxProps, Container, ContainerProps } from '@mui/material';
import { ControlsStringAssets, MessagesStringAssets, PageAndViewTitleStringAssets } from '../../../../assets/stringAssets';
import { enumAlertType } from '../../../enums';
import { IAudioLink } from '../../../../dataObjects/models/digitalMedia/AudioLink';
import { typeUniqueId } from '../../../../dataObjects/types';
import { IStoreState } from '../../../../uiMiddleware-redux/store/IStoreState';
import { enumBeaconType, enumCollectionViewType, enumWorkflowState } from '../../../../dataObjects/enums';
import TwoButtonAcceptanceDialog from '../../../dialogs/TwoButtonAcceptanceDialog/TwoButtonAcceptanceDialog';
import { Beacon } from '../../../../dataObjects/models/alerts/Beacon';
import { useReduxWorkflowState } from '../../../customHooks';
import ActivityIndicatorDialog from '../../../dialogs/ActivityIndicatorDialog/ActivityIndicatorDialog';
import { IActivityIndicatorData } from '../../../dialogs/ActivityIndicatorData';
import { AlertInfo, IAlertInfo } from '../../../../dataObjects/models/alerts/AlertInfo';
import { useGetCollectionViewType } from '../../../customHooks/useGetCollectionViewType';
import GenericToggleViewContainer from '../../GenericToggleViewContainer/GenericToggleViewContainer';
import { AudioLinksCardGridView } from '../AudioLinksCardGridView/AudioLinksCardGridView';
import AudioLinksListView from '../AudioLinksListView/AudioLinksListView';
import { styled } from '@mui/styles';
import { IAudioLinkViewModel } from '../../../../dataObjects/viewModels/audioLinkViewModel';
import { useAppDispatch } from '../../../../uiMiddleware-redux/store/configureStore';
import { audioLinkDeleteRequest, audioLinkDeleteStatusChange } from '../../../../uiMiddleware-redux/slices/audioLink/audioLinkDeleteStatusSlice';
import { beaconChange } from '../../../../uiMiddleware-redux/slices/beacon/beaconSlice';
import { alertInfoChange } from '../../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area padding at the top of the view
const StyledBoxForPaddingAtopView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// a styled Container for the View Container
const StyledContainerForViewContainer = styled((props: ContainerProps) => (
  <Container
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
  paddingLeft: 0,
  paddingRight: 0,
}));

export interface IAudioLinksViewProps extends PropsWithChildren<unknown> {
  parentId?: typeUniqueId; // the parent DigitalMedia for the AudioLinks
  audioLinksToDisplay: Array<IAudioLinkViewModel>;
  allowAddingNewItems: boolean;
  audioLinksDataLoading: boolean;
}

const AudioLinksView: React.FC<IAudioLinksViewProps> = (props: IAudioLinksViewProps) => {

  AudioLinksView.displayName = 'Audio Links View';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const { parentId, audioLinksToDisplay, allowAddingNewItems, audioLinksDataLoading } = props;

  // call custom hook to get the current view type, so the value can be used to initialize the View Type state variable
  const collectionViewType: enumCollectionViewType = useGetCollectionViewType();

  // the AudioLinkViewModels associcated with the current Digital Media
  // const [audioLinks, setAudioLinks] = useState<Array<IAudioLink>>(audioLinksToDisplay);
  const [audioLinkViewModels, setAudioLinkViewModels] = useState<Array<IAudioLinkViewModel>>(audioLinksToDisplay);
  // whether data is currently loading
  const [dataLoading, setDataLoading] = useState<boolean>(audioLinksDataLoading);
  // if user is deleting a AudioLink, the AudioLink to delete
  const [audioLinkToDelete, setAudioLinkToDelete] = useState<IAudioLink | undefined>(undefined);
  // whether to show the Delete Confirmation dialog
  const [showDeleteConfirmationDlg, setShowDeleteConfirmationDlg] = useState<boolean>(false);

  // for dispatching redux actions
  const dispatch = useAppDispatch();

  /**
   * @function resetDeleteAudioLinkStatus Resets the Delete status in Redux state
   */
  const resetDeleteAudioLinkStatus: () => void = useCallback(() => {
    dispatch(audioLinkDeleteStatusChange(null));
  }, [dispatch])


  // use a custom hook to evaluate the redux state for the deleteAudioLink workflow and any deleteAudioLink error
  const { workflowState: deleteAudioLinkStatus, errorState: deleteAudioLinkFailure } = useReduxWorkflowState((state: IStoreState) => state.deleteAudioLinkStatus, (state: IStoreState) => state.deleteAudioLinkFailure);

  useEffect(() => {
    if (audioLinkViewModels !== audioLinksToDisplay) {
      setAudioLinkViewModels(audioLinksToDisplay);
    }
  }, [audioLinksToDisplay, audioLinkViewModels]);

  useEffect(() => {
    if (dataLoading !== audioLinksDataLoading) {
      setDataLoading(audioLinksDataLoading);
    }
  }, [audioLinksDataLoading, dataLoading]);

  /**
   * @method handleAudioLinkDelete Handles requests to delete a Channel
   * @param audioLink The AudioLink to be deleted
   */
  async function handleAudioLinkDelete(audioLink: IAudioLink): Promise<void> {
    // set the audioLink to be deleted into local state
    setAudioLinkToDelete(audioLink);

    // set flag to show the delete confirmation dialog
    setShowDeleteConfirmationDlg(true);
  }

  function handleAudioLinkDeleteConfirmed(): void {

    // using the audioLink object to be deleted from the local state, dispatch an action to delete the audioLink object
    if (audioLinkToDelete !== undefined) {
      dispatch(audioLinkDeleteRequest(audioLinkToDelete));
    }

    // call method to reset the audioLink delete local state details
    audioLinkDeleteReset();
  }

  function handleAudioLinkDeleteCanceled(): void {
    // call method to reset the audioLink delete local state details
    audioLinkDeleteReset();
  }

  function audioLinkDeleteReset() {
    // reset the audioLink to be deleted to 'undefined' in local state, since we're done with the object
    setAudioLinkToDelete(undefined);

    // set flag to hide the delete confirmation dialog
    setShowDeleteConfirmationDlg(false);
  }


  // data to indicate whether an activity indicator should be displayed and, if so, the message
  let activityIndicatorData: IActivityIndicatorData = {
    showActivityIndicator: false,
    activityIndicatorMessage: ''
  }

  // data to indicate whether an alert should be displayed
  let alertInfo: IAlertInfo | undefined = undefined;

  // indicates whether the object has been successfully deleted
  const [successfullyDeletedObject, setSuccessfullyDeletedObject] = useState<boolean>(false);

  // define an effect that will process whenever the successfullyCompletedPage state changes
  useEffect(() => {
    if (successfullyDeletedObject) {
      // dispatch an action to reset the deleteTopicStatus
      resetDeleteAudioLinkStatus();

      // notify user via a Beacon notification that the audioLink has been deleted
      dispatch(beaconChange(new Beacon(undefined, enumBeaconType.Success, MessagesStringAssets.audioLink_DeleteConfirmationHeader, MessagesStringAssets.audioLink_DeleteSuccess)));

      // reset the successfullyDeletedObject flag
      setSuccessfullyDeletedObject(false);
    }
  }, [successfullyDeletedObject, dispatch, resetDeleteAudioLinkStatus]);

  if (dataLoading) {
    // display an activity indicator
    activityIndicatorData.showActivityIndicator = true;
    activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.audioLinks_FetchingRequested;
  } else {
    // if the workflow is in the midst of performing deletion of a audioLink, prepare an alert message fragment to display progress message(s)
    if (deleteAudioLinkStatus !== undefined && deleteAudioLinkStatus !== null) {
      if (deleteAudioLinkStatus === enumWorkflowState.Requested || deleteAudioLinkStatus === enumWorkflowState.InProgress) {
        // display an activity indicator
        activityIndicatorData.showActivityIndicator = true;
        activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.audioLink_DeleteRequested;
      } else if (deleteAudioLinkStatus === enumWorkflowState.Success) {
        // if the successfullyDeletedObject is curently in a 'false' state, we'll want the flag to indicate that the deletion was a success
        if (!successfullyDeletedObject) {
          // set flag to indicate that the object has been successfully deleted, which will force a re-render to allow for cleanup and user notification
          setSuccessfullyDeletedObject(true);
        }
      } else if (deleteAudioLinkStatus === enumWorkflowState.Failure) {
        // display an alert message
        // alertFragment = generateJsxAlertMessageFragment(MessagesStringAssets.audioLink_DeleteFailure, enumJsxAlertMessageDisplayVariants.Failure, false, deleteAudioLinkFailureMessage, MessagesStringAssets.substitutionKeyword);
        let failureMessage = 'Unknown error';
        if (deleteAudioLinkFailure && deleteAudioLinkFailure instanceof Error) {
          failureMessage = deleteAudioLinkFailure.message;
        }
        alertInfo = new AlertInfo(true, enumAlertType.Error, failureMessage);
        dispatch(alertInfoChange(alertInfo));
      }
    }
  }


  // Establish the text to be displayed on the '+ New' button
  let addNewButtonText: string = '';
  if (allowAddingNewItems) {
    addNewButtonText = ControlsStringAssets.audioNew;
  }


  return (
    <>
      <StyledBoxForPaddingAtopView />
      <GenericToggleViewContainer
        viewTitle={PageAndViewTitleStringAssets.viewTitle_Audios}
        addNewButtonText={addNewButtonText}
        urlForAddNewButtonNavigation={`/${parentId}/audioLink`}
      >
        {/* Container for embedded data view */}
        <StyledContainerForViewContainer>

          {/* If there are AudioLinks to display and the current view selection is CardsGridView, display the AudioLinksCardGridView */}
          {audioLinkViewModels &&
            (collectionViewType === enumCollectionViewType.CardsGridView) &&
            <AudioLinksCardGridView audioLinkViewModels={audioLinkViewModels} onDelete={handleAudioLinkDelete} />
          }

          {/* If there are AudioLinks to display and the current view selection is ListView, display the AudioLinksListView */}
          {audioLinkViewModels &&
            (collectionViewType === enumCollectionViewType.ListView) &&
            <AudioLinksListView audioLinkViewModels={audioLinkViewModels} onDelete={handleAudioLinkDelete} />
          }
        </StyledContainerForViewContainer>

        {/* Delete AudioLink Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showDeleteConfirmationDlg}
          headerText={MessagesStringAssets.audioLink_DeleteConfirmationHeader}
          bodyText={MessagesStringAssets.audioLink_DeleteConfirmation}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handleAudioLinkDeleteConfirmed}
          onNonAcceptance={handleAudioLinkDeleteCanceled}
        />

        {activityIndicatorData.showActivityIndicator &&
          <ActivityIndicatorDialog activityLabel={activityIndicatorData.activityIndicatorMessage} />
        }

      </GenericToggleViewContainer>
    </>
  );
}

export default AudioLinksView;
